import React, { useEffect, useRef, useCallback } from 'react';

interface Message {
  generated_html?: string;
}

interface PlotComponentProps {
  message: Message;
  plotInstanceRef: React.MutableRefObject<any>;
  plotContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  isFullscreenDialog?: boolean;
  viewMode?: {
    isFullscreen: boolean;
    isSidePanel: boolean;
    isChatMode: boolean;
  };
}

const MyPlotComponent: React.FC<PlotComponentProps> = ({ 
  message, 
  plotInstanceRef, 
  plotContainerRef,
  isFullscreenDialog = false,
  viewMode
}) => {
  const isPlotlyTable = useCallback((generatedHtml: string) => {
    try {
      return generatedHtml.includes('"type":"table"');
    } catch (error) {
      console.error('Error detecting if plot is a table:', error);
      return false;
    }
  }, []);

  const resizePlot = useCallback(() => {
    if (plotInstanceRef.current && window.Plotly) {
      // Check if the plot div exists and is displayed
      const plotDiv = plotInstanceRef.current.el;
      if (plotDiv && plotDiv.offsetParent !== null) {
        try {
          window.Plotly.Plots.resize(plotInstanceRef.current);
        } catch (error) {
          console.warn('Failed to resize plot:', error);
        }
      }
    }
  }, [plotInstanceRef]);

  useEffect(() => {
    async function renderPlot() {
      if (!message.generated_html || !plotContainerRef.current) return;

      // Don't clear container if it already has a plot and we're not in fullscreen
      if (!isFullscreenDialog && plotContainerRef.current.querySelector('.js-plotly-plot')) {
        return;
      }

      console.log('Attempting to create plot...');

      // Clear any existing content
      plotContainerRef.current.innerHTML = '';

      const plotDiv = document.createElement('div');
      plotDiv.style.width = '100%';
      plotDiv.style.height = '100%';
      plotContainerRef.current.appendChild(plotDiv);

      const parser = new DOMParser();
      const doc = parser.parseFromString(message.generated_html, 'text/html');

      // Find the script with Plotly.newPlot
      const scripts = doc.querySelectorAll('script:not([src])');
      let plotlyScript = '';
      scripts.forEach(script => {
        if (script.textContent?.includes('Plotly.newPlot')) {
          plotlyScript = script.textContent;
        }
      });

      if (!plotlyScript) {
        console.error('No Plotly script found in the provided HTML.');
        return;
      }

      // Extract the target div id from the snippet
      const plotContainer = doc.querySelector('.plotly-graph-div');
      const targetId = plotContainer?.id;

      if (!targetId) {
        console.error('No plot container id found.');
        return;
      }

      // Ensure Plotly is loaded
      if (typeof window.Plotly === 'undefined') {
        try {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.plot.ly/plotly-2.35.2.min.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Plotly from CDN.'));
            document.head.appendChild(script);
          });
        } catch (err) {
          console.error('Could not load Plotly:', err);
          return;
        }
      }

      // Create a temporary div with the target id to pass the if-check
      let tempDiv = document.getElementById(targetId);
      if (!tempDiv) {
        tempDiv = document.createElement('div');
        tempDiv.id = targetId;
        // Hide this div so it doesn't affect layout
        tempDiv.style.display = 'none';
        document.body.appendChild(tempDiv);
      }

      // Run the script in a sandbox to capture args
      try {
        const sandboxFunction = new Function('Plotly', 'document', `
          let capturedId, capturedData, capturedLayout, capturedConfig;
          const originalNewPlot = Plotly.newPlot;
          Plotly.newPlot = (id, data, layout, config) => {
            capturedId = id;
            capturedData = data;
            capturedLayout = layout;
            capturedConfig = config;
          };

          ${plotlyScript};

          Plotly.newPlot = originalNewPlot;
          return { id: capturedId, data: capturedData, layout: capturedLayout, config: capturedConfig };
        `);

        const { id, data, layout, config } = sandboxFunction(window.Plotly, document);

        if (!data || !layout) {
          console.error('Failed to extract plot data or layout from the script.');
          return;
        }

        // Update layout for better responsiveness
        const finalLayout = {
          ...layout,
          autosize: true,
          // Remove fixed height/width to allow responsive sizing
          margin: {
            l: 50,
            r: 30,
            t: 75,
            b: 50,
            pad: 4
          }
        };

        // Delete any fixed dimensions
        delete finalLayout.height;
        delete finalLayout.width;
        
        const finalConfig = {
          ...config,
          responsive: true,
          useResizeHandler: true,
          displayModeBar: true,
          modeBarButtonsToRemove: ['autoScale2d'],
          displaylogo: false,
        };

        plotInstanceRef.current = await window.Plotly.newPlot(plotDiv, data, finalLayout, finalConfig);
        
        // Wait for next frame before attempting resize
        requestAnimationFrame(() => {
          if (plotInstanceRef.current && plotInstanceRef.current.el) {
            resizePlot();
          }
        });
      } catch (error) {
        console.error('Failed to create plot with sandbox approach:', error);
      } finally {
        // Clean up the temporary div if we created it
        if (tempDiv && tempDiv.parentNode) {
          tempDiv.parentNode.removeChild(tempDiv);
        }
      }
    }

    renderPlot();

    return () => {
      // Only clean up if this is the fullscreen instance
      if (isFullscreenDialog && plotInstanceRef.current && window.Plotly) {
        try {
          window.Plotly.purge(plotInstanceRef.current);
          plotInstanceRef.current = null;
        } catch (error) {
          console.warn('Failed to purge plot:', error);
        }
      }
    };
  }, [message.generated_html, isPlotlyTable, plotContainerRef, plotInstanceRef, isFullscreenDialog]);

  // Update the useEffect that handles view mode changes
  useEffect(() => {
    // Ensure resize happens after DOM updates
    const timeoutId = setTimeout(resizePlot, 150);
    return () => clearTimeout(timeoutId);
  }, [resizePlot, viewMode]);

  return (
    <div 
      ref={plotContainerRef} 
      style={{ 
        width: '100%',
        // height: '400px',
        position: 'relative',
        overflow: 'hidden'
      }}
    />
  );
};

export default MyPlotComponent;